/**
 * ItemLocationsSection 各地點可供量 (SR3.2, DD!179 Must) — a read-only Tier-1
 * NetSuite availability read-through, echoed per 地點 (location).
 *
 * The ERP web never owns on-hand (two-tier boundary, Fixed decision 3): every
 * number here is a NetSuite echo carrying `syncedAt`, and NONE of them drill —
 * there are deliberately no on-hand / warehouse surfaces to drill into. Zero
 * write affordances. The bold totals row is server-computed; its 可供量 sums to
 * the header `availableQty` by construction.
 *
 * States are defined UP FRONT (not discovered): LOADING (skeleton), hard-ERROR
 * (retry), empty (product not linked to a NetSuite item), and a stale badge
 * when the snapshot ages past the freshness threshold. The snapshot stays
 * VISIBLE when stale — the read-through is advisory, never blocking.
 *
 * The 在途調撥 (in-transit transfer) column is HELD (OQ-18): inter-location
 * movement language on a Tier-2 surface with no warehouse surfaces is a
 * boundary/vocab call routed to the owner. The field, contract, and i18n key
 * already exist, so un-holding it is adding one column object here — no rework.
 *
 * OQ-6 deferral LIFTED for four columns (S6.1): the col-J authority — xlsx
 * `20260708 產品主檔 欄位.xlsx` col J MoSCoW (R&D, the verified 74-Must triage
 * that supersedes the earlier V3-K / V7-F triages) — marks DD!75 現有量 /
 * DD!76 基本單位現有量 / DD!77 帳面價值 / DD!78 平均成本 as Must, un-deferring
 * exactly those four read-through columns. Committed stays undrawn: no col-J
 * row cites it. Labels are the S0.2 sanctioned pairs in `ofcVocabulary.ts`
 * (the drawn zh label for DD!77 contains a banned term and NEVER ships —
 * 帳面價值 is the sanctioned relabel). Still under the sanctioned read-through
 * contract: SourceStampTag + stale badge unchanged, zero write affordances.
 *
 * DD!188 grid 編輯 control: build-default HIDDEN (Q-M3, hide-don't-disable) —
 * a row-Edit affordance has no target on a read-only Tier-2 read-through lens
 * (same ruling as its DD!161 Transactions twin). No edit control renders here.
 *
 * Zero-vs-empty trap (plan §S6.1, ux-lens finding 4): the four server-computed
 * On Hand totals render dashes (`renderDash`) whenever the payload is empty or
 * stale — server-computed totals are never silently rendered as 0 when the
 * read-through has no data. `selectOnHandTotalsDisplay` owns that decision;
 * the weighted 平均成本 total with ΣqtyOnHand === 0 is the server's no-data
 * sentinel (0) and also renders a dash, never a cost of 0.00.
 *
 * DD!42 / DD!120 unit qualifiers (Y-S1, 0708-J col-J SHOULD): the 現有量 and
 * 平均成本 columns carry DISPLAY-ONLY unit suffixes echoed from the product
 * master — `product.stockUnit` for the quantity (DD!42 Total Qty On Hand
 * Unit) and currency-per-stock-unit for the cost (DD!120 Average Cost Unit) —
 * on the column headers and the totals row. The crediting decision: the unit
 * is IMPLICIT product data already, so no new stored field exists anywhere;
 * `selectOnHandUnitSuffixes` composes strings only (engine purity — no
 * arithmetic). A product without the source values renders the plain
 * sanctioned headers, and a dashed (empty/stale) total never carries a
 * qualifier — the renderDash contract extends to its suffix.
 *
 * Self-contained: drops into the detail-page Sider by the SR4.1 wiring pass
 * with a single `<ItemLocationsSection product={product} />` line.
 */

import { Alert, Empty, Skeleton, Space, Table, theme } from 'antd';
import type { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListErrorState, renderDash } from '@/components/common/TableContainer';
import SourceStampTag from '@/components/common/SourceStampTag';
import { freshnessTierOf, isStaleTier } from '@/lib/reorder/staleness';
import { useItemLocations } from '@/hooks/useItemLocations';
import type {
  NetSuiteItemLocationOnHandRow,
  NetSuiteItemLocationOnHandTotals,
  NetSuiteItemLocationRow,
  NetSuiteItemLocationTotals,
} from '@/types/api/purchaseOrder';
import type { Product } from '@/types/api/product';
import SectionHeader from './SectionHeader';

/**
 * DD!75–78 typing — Y-S0 promoted the extended row/totals interfaces into
 * `src/types/api/purchaseOrder.ts` (the recorded S1.1 followUp is closed), so
 * the field-by-field local shim is gone: these aliases DERIVE from the single
 * promoted source. The extension keys stay optional at THIS boundary only
 * because `useItemLocations` still types the fetch with the base
 * `NetSuiteItemLocationsResponse` contract (hook retype = recorded followUp) —
 * and optionality remains the render contract regardless: a payload without
 * the extension renders dashes, never fabricated zeros.
 */
export type ItemLocationOnHandRow = NetSuiteItemLocationRow &
  Partial<Omit<NetSuiteItemLocationOnHandRow, keyof NetSuiteItemLocationRow>>;

/** Server-computed totals + the optional DD!75–78 extension (same derivation). */
export type ItemLocationOnHandTotals = NetSuiteItemLocationTotals &
  Partial<Omit<NetSuiteItemLocationOnHandTotals, keyof NetSuiteItemLocationTotals>>;

/* eslint-disable react-refresh/only-export-components -- the column contract
   and the totals display selector are data/pure exports consumed by this
   slice's tests and the S8.2 closure test (registry-as-data doctrine). */

/**
 * The DD!75–78 On Hand column contract, in DD order — registry-as-data so the
 * S8.2 closure test can credit each xlsx row to this surface mechanically.
 * Every number rendered from these keys is a SERVER-computed NetSuite echo
 * (engine purity: no arithmetic happens in this component).
 *
 * The `unit` entries carry the DD!42 / DD!120 unit-qualifier provenance
 * (0708-J col-J SHOULD, moscow:'should' — display-only, never gates 產品就緒):
 * `selectOnHandUnitSuffixes` derives the header/totals suffixes FROM this
 * data, so the closure test credits both rows off the same contract.
 */
export const ITEM_LOCATION_ON_HAND_COLUMNS = [
  {
    key: 'qtyOnHand',
    ddRow: 75,
    labelKey: 'products:itemLocations.columns.qtyOnHand',
    decimals: 0,
    width: 150,
    /** DD!42 Total Qty On Hand Unit — suffix from `product.stockUnit`. */
    unit: { ddRow: 42, source: 'stockUnit' },
  },
  {
    key: 'qtyOnHandBaseUnit',
    ddRow: 76,
    labelKey: 'products:itemLocations.columns.qtyOnHandBaseUnit',
    decimals: 0,
    width: 170,
  },
  {
    key: 'value',
    ddRow: 77,
    labelKey: 'products:itemLocations.columns.value',
    decimals: 2,
    width: 130,
  },
  {
    key: 'averageCost',
    ddRow: 78,
    labelKey: 'products:itemLocations.columns.averageCost',
    decimals: 2,
    width: 190,
    /** DD!120 Average Cost Unit — currency-per-stock-unit suffix. */
    unit: { ddRow: 120, source: 'currencyPerStockUnit' },
  },
] as const;

type OnHandColumnKey = (typeof ITEM_LOCATION_ON_HAND_COLUMNS)[number]['key'];

/** Per-cell verdict for the totals row: a number may render, or a dash MUST. */
export type OnHandTotalsDisplay = Record<OnHandColumnKey, number | null>;

const ON_HAND_TOTALS_DASHED: OnHandTotalsDisplay = {
  qtyOnHand: null,
  qtyOnHandBaseUnit: null,
  value: null,
  averageCost: null,
};

/**
 * Zero-vs-empty trap gate (plan §S6.1 acceptance): decides whether each
 * server-computed On Hand total renders as a NUMBER or as a dash.
 *
 * - Stale snapshot, empty payload (no location rows), or a payload without the
 *   DD!75–78 extension ⇒ ALL four cells dash — no data is never rendered 0.
 * - ΣqtyOnHand === 0 ⇒ the value-weighted 平均成本 is undefined; the server
 *   sends a 0 sentinel which must render as a dash, never as a cost of 0.00.
 *   (qtyOnHand / 帳面價值 totals of 0 over REAL rows are genuine data.)
 *
 * Pure and exported so the empty/stale contract is directly test-pinned.
 */
export const selectOnHandTotalsDisplay = (
  totals: ItemLocationOnHandTotals | undefined,
  { stale, rowCount }: { stale: boolean; rowCount: number }
): OnHandTotalsDisplay => {
  if (stale || rowCount === 0 || !totals || totals.qtyOnHand === undefined) {
    return ON_HAND_TOTALS_DASHED;
  }
  return {
    qtyOnHand: totals.qtyOnHand,
    qtyOnHandBaseUnit: totals.qtyOnHandBaseUnit ?? null,
    value: totals.value ?? null,
    averageCost: totals.qtyOnHand > 0 ? (totals.averageCost ?? null) : null,
  };
};

/**
 * DD!42 / DD!120 unit qualifiers (Y-S1, display-only): per-column header /
 * totals suffixes echoed from the product master, derived FROM the column
 * contract's `unit` provenance entries. Pure string composition — engine
 * purity holds (no arithmetic), and no new stored field exists: the unit IS
 * `product.stockUnit` / `product.currency` (the disposition's crediting
 * decision).
 *
 * Never fabricates a qualifier (the renderDash-on-empty analog): a blank
 * stock unit drops the DD!42 suffix; the DD!120 cost suffix needs a currency
 * to say anything (currency-per-stock-unit when both exist, bare currency
 * when the stock unit is unusable, nothing without a currency).
 */
export const selectOnHandUnitSuffixes = (
  product: Pick<Product, 'stockUnit' | 'currency'>
): Partial<Record<OnHandColumnKey, string>> => {
  const stockUnit = product.stockUnit.trim() || undefined;
  const currency = product.currency?.trim() || undefined;
  const suffixes: Partial<Record<OnHandColumnKey, string>> = {};
  for (const column of ITEM_LOCATION_ON_HAND_COLUMNS) {
    if (!('unit' in column)) continue;
    const suffix =
      column.unit.source === 'stockUnit'
        ? stockUnit
        : currency && stockUnit
          ? `${currency}/${stockUnit}`
          : currency;
    if (suffix) {
      suffixes[column.key] = suffix;
    }
  }
  return suffixes;
};

/* eslint-enable react-refresh/only-export-components */

interface ItemLocationsSectionProps {
  product: Product;
}

/**
 * Neutral, width-bounded numeric cell (tabular figures; no colour-by-value).
 * Nullish values render the list-kit dash — the empty/stale read-through
 * contract for the DD!75–78 cells rides this branch.
 */
const NumericCell = ({
  value,
  bold = false,
  decimals,
}: {
  value?: number | null;
  bold?: boolean;
  decimals?: number;
}) => {
  const { token } = theme.useToken();
  if (value === undefined || value === null) {
    return <span>{renderDash(value)}</span>;
  }
  return (
    <span
      style={{
        fontVariantNumeric: 'tabular-nums',
        fontWeight: bold ? token.fontWeightStrong : undefined,
      }}
    >
      {decimals === undefined
        ? value.toLocaleString()
        : value.toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          })}
    </span>
  );
};

export const ItemLocationsSection = ({ product }: ItemLocationsSectionProps) => {
  const { t } = useTranslation(['products']);
  const { token } = theme.useToken();
  const { rows, totals, netSuiteItemId, syncedAt, error, isLoading, refresh } = useItemLocations(
    product.id
  );

  // Freshness is owned solely by this read-through tier (advisory, never blocks).
  const stale = syncedAt ? isStaleTier(freshnessTierOf(syncedAt, new Date().toISOString())) : false;

  // DD!42 / DD!120 display-only unit qualifiers — echoed from the product
  // master, never stored, never computed (string composition only).
  const unitSuffixes = selectOnHandUnitSuffixes(product);

  const columns: TableProps<ItemLocationOnHandRow>['columns'] = [
    {
      // 地點 — AU location name (a proper noun echo, not chrome).
      title: t('products:itemLocations.columns.location'),
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: t('products:itemLocations.columns.availableQty'),
      dataIndex: 'availableQty',
      key: 'availableQty',
      width: 140,
      align: 'right',
      render: (value: number) => <NumericCell value={value} />,
    },
    {
      title: t('products:itemLocations.columns.qtyOnOrder'),
      dataIndex: 'qtyOnOrder',
      key: 'qtyOnOrder',
      width: 160,
      align: 'right',
      render: (value: number) => <NumericCell value={value} />,
    },
    // HELD: 在途調撥 (inTransit) column — OQ-18 boundary/vocab ruling pending.
    // Add its column object here once the owner rules; data + i18n already exist.
    {
      title: t('products:itemLocations.columns.backOrdered'),
      dataIndex: 'backOrdered',
      key: 'backOrdered',
      width: 130,
      align: 'right',
      render: (value: number) => <NumericCell value={value} />,
    },
    // DD!75–78 On Hand read-through columns (S6.1) — appended in DD order.
    // Server-computed echoes only; DD!188 grid 編輯 stays HIDDEN (Q-M3).
    // DD!42/DD!120 headers carry the display-only unit qualifier when the
    // product master provides one; otherwise the plain sanctioned label.
    ...ITEM_LOCATION_ON_HAND_COLUMNS.map(({ key, labelKey, decimals, width }) => ({
      title: unitSuffixes[key]
        ? t('products:itemLocations.columns.headerWithUnit', {
            label: t(labelKey),
            unit: unitSuffixes[key],
          })
        : t(labelKey),
      dataIndex: key,
      key,
      width,
      align: 'right' as const,
      render: (value?: number) => <NumericCell value={value} decimals={decimals} />,
    })),
  ];

  const renderBody = () => {
    // Hard-ERROR up front — the read-through failed; offer a retry.
    if (error) {
      return (
        <ListErrorState
          title={t('products:itemLocations.states.loadFailed')}
          retryLabel={t('products:itemLocations.states.retry')}
          onRetry={refresh}
        />
      );
    }

    // LOADING up front — skeleton until the first read-through resolves.
    if (isLoading && !syncedAt) {
      return <Skeleton active paragraph={{ rows: 5 }} />;
    }

    // Empty — the product is not mapped to a NetSuite item, so there is no
    // availability to echo (a DEFINED response, not an error).
    if (!netSuiteItemId) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('products:itemLocations.states.empty')}
        />
      );
    }

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {stale && (
          <Alert
            type="warning"
            showIcon
            message={t('products:itemLocations.states.stale')}
          />
        )}
        <TableContainer>
          <Table<ItemLocationOnHandRow>
            rowKey="location"
            size="small"
            dataSource={rows}
            columns={columns}
            pagination={false}
            scroll={{ x: 1200 }}
            summary={() => {
              if (!totals) return null;
              const onHandDisplay = selectOnHandTotalsDisplay(totals, {
                stale,
                rowCount: rows.length,
              });
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ background: token.colorFillQuaternary }}>
                    <Table.Summary.Cell index={0}>
                      <span style={{ fontWeight: token.fontWeightStrong }}>
                        {t('products:itemLocations.totals')}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <NumericCell value={totals.availableQty} bold />
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <NumericCell value={totals.qtyOnOrder} bold />
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <NumericCell value={totals.backOrdered} bold />
                    </Table.Summary.Cell>
                    {ITEM_LOCATION_ON_HAND_COLUMNS.map((column, position) => {
                      const totalValue = onHandDisplay[column.key];
                      const unitSuffix = unitSuffixes[column.key];
                      return (
                        <Table.Summary.Cell key={column.key} index={4 + position} align="right">
                          <NumericCell value={totalValue} bold decimals={column.decimals} />
                          {/* DD!42/DD!120 qualifier suffixes REAL totals only —
                              a dashed (empty/stale) cell never carries one. */}
                          {unitSuffix !== undefined && totalValue !== null && (
                            <span
                              style={{
                                marginLeft: token.marginXXS,
                                color: token.colorTextSecondary,
                                fontSize: token.fontSizeSM,
                              }}
                            >
                              {unitSuffix}
                            </span>
                          )}
                        </Table.Summary.Cell>
                      );
                    })}
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </TableContainer>
      </Space>
    );
  };

  return (
    <div>
      <SectionHeader
        title={t('products:itemLocations.title')}
        subtitle={t('products:itemLocations.subtitle')}
        extra={
          netSuiteItemId && syncedAt ? (
            <SourceStampTag
              sourceLabel={t('products:itemLocations.source')}
              syncedAt={syncedAt}
            />
          ) : undefined
        }
      />
      {renderBody()}
    </div>
  );
};

export default ItemLocationsSection;
